import { inject, Injectable } from '@angular/core';
import { SawDust } from '@cudirect/sawdust/sawdust';

/**
 * Service to handle timing events and logging their durations.
 * @remarks This service is to be used in two cases:
 * ONE - If we need to measure an event that starts before Angular / UEM services are available (index.html)
 * TWO - If "offical" UEM timing is not required, and we just want to do a simple duration track event in AI
 */
@Injectable({
  providedIn: 'root'
})
export class TimerService {
  private logger = inject(SawDust);
  private logContext(additional: Record<string, unknown> = {}) {
    return { component: 'TimerService', ...additional };
  }

  constructor() {}

  /**
   * Starts a timer for a given event - events can also be started manually using performance.mark
   * If this is manually started, the naming convention of `${eventName}-start` should be adhered to.
   * @param eventName - The name of the event to start timing.
   */
  startTimer(eventName: string): void {
    const startMark = this.getMarkName(eventName, 'start');
    performance.mark(startMark);
    this.logger.debug('Started performance timer', this.logContext({ eventName }));
  }

  /**
   * Ends the timer for a given event and measures the elapsed time.
   * Assumes that the startTimer method has been called (`${eventName}-start`) for the same event (manually or via service)
   * @param eventName - The name of the event to end timing.
   * @param appInsightsEventName - Optional name for the event to log in App Insights. If not passed, the event will not be logged.
   * @returns The duration of the event in milliseconds, or undefined if the start mark was not found.
   */
  endTimerAndMeasure(eventName: string, appInsightsEventName?: string): number | undefined {
    // Set up vars
    const startMark = this.getMarkName(eventName, 'start');
    const endMark = this.getMarkName(eventName, 'end');

    // Check if the start mark exists
    if (!performance.getEntriesByName(startMark).length) {
      this.logger.warn('Performance start mark not found; unable to measure duration', this.logContext({ eventName, startMark }));
      return;
    }

    try {
      /** Value to be returned as time duration */
      let timer: number | undefined;

      // Mark the end of the event
      performance.mark(endMark);

      // Measure the duration between the start and end marks
      performance.measure(eventName, startMark, endMark);

      // Get the performance entry for the measured duration
      const measureEntries = performance.getEntriesByName(eventName);

      // Extract and round the duration if available
      if (measureEntries.length > 0) {
        timer = Math.floor(measureEntries[0].duration);
      }

      // Clear the performance marks and measures to avoid memory leaks
      performance.clearMarks(startMark);
      performance.clearMarks(endMark);
      performance.clearMeasures(eventName);

      // Log the duration to App Insights if an event name is provided
      if (appInsightsEventName && timer !== undefined) {
        this.logger.logEvent(appInsightsEventName, {
          duration: timer.toString()
        });
      }
      this.logger.debug('Measured performance timer', this.logContext({ eventName, durationMs: timer }));
      return timer;
    } catch (error) {
      this.logger.error('Error measuring timer duration', error, this.logContext({ eventName }));
    }
  }

  /**
   * Constructs a performance mark name using the provided event name and a suffix.
   *
   * This method concatenates the `eventName` with a `suffix`, separated by a hyphen,
   * to create a standardized naming convention for performance marks.
   *
   * @param eventName - The name of the event.
   * @param suffix - The suffix to append to the event name, typically 'start' or 'end'.
   * @returns The concatenated string forming the performance mark name.
   * @remarks I hate this but SonarQube is forcing me to do it, it doesn't like template literals.
   */
  private getMarkName(eventName: string, suffix: 'start' | 'end'): string {
    return `${eventName}-${suffix}`;
  }
}
